import React, { useState, useMemo, useEffect } from 'react';
import { List, ListItemButton, ListItemText, Collapse, ListItemIcon, Box } from '@mui/material';
import { ExpandLess, ExpandMore } from '@mui/icons-material';
import FolderIcon from '@mui/icons-material/Folder';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';

const ProjectIcon = () => <FolderIcon sx={{ color: '#F1C40F', fontSize: 16 }} />;
const SubProjectIcon = () => <InsertDriveFileIcon sx={{ color: '#F39C12', fontSize: 16 }} />;

interface TreeItemProps {
    item: any;
    onSelect: (item: any) => void;
    selectedId: string | null;
    forceHighlight?: boolean;
}

const TreeItem: React.FC<TreeItemProps> = ({ item, onSelect, selectedId, forceHighlight }) => {

    const isSelectedOrAncestor = useMemo(() => {
        const id = item.CodProyecto || item.CodClasificacion || item.CodInsumo;
        if (selectedId === id) return true;

        const check = (nodes: any[]): boolean => {
            return nodes.some(n => {
                const nId = n.CodProyecto || n.CodClasificacion || n.CodInsumo;
                return (nId === selectedId) || (n.items && check(n.items));
            });
        };
        return item.items ? check(item.items) : false;
    }, [item, selectedId]);

    const myId = item.CodProyecto || item.CodClasificacion || item.CodInsumo;
    const isExactSelected = selectedId === myId;

    const [open, setOpen] = useState(isSelectedOrAncestor);

    useEffect(() => {
        if (isSelectedOrAncestor) {
            setOpen(true);
        }
    }, [isSelectedOrAncestor]);

    const hasChildren = item.items && item.items.length > 0;

    const handleExpandClick = (e: React.MouseEvent) => {
        e.stopPropagation();
        setOpen(!open);
    };

    const handleItemClick = (e: React.MouseEvent) => {
        e.stopPropagation();
        onSelect(item);
    };

    const shouldHighlight = isExactSelected || forceHighlight;

    return (
        <>
            <ListItemButton
                onClick={handleItemClick}
                sx={{
                    pl: `${(item.Nivel - 1) * 12 + 8}px`,
                    py: 0,
                    minHeight: 24,
                    bgcolor: shouldHighlight ? 'rgba(46, 134, 193, 0.15) !important' : 'transparent',
                    '&:hover': {
                        bgcolor: shouldHighlight ? 'rgba(46, 134, 193, 0.25) !important' : 'rgba(0, 0, 0, 0.04)'
                    },
                    '& .MuiListItemIcon-root': {
                        minWidth: 20,
                        mr: 0.5
                    }
                }}
            >
                <Box
                    component="span"
                    onClick={hasChildren ? handleExpandClick : undefined}
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        cursor: hasChildren ? 'pointer' : 'default',
                        p: 0.5,
                        borderRadius: '50%',
                        '&:hover': { bgcolor: hasChildren ? 'rgba(0,0,0,0.05)' : 'transparent' }
                    }}
                >
                    {hasChildren ? (
                        open ? <ExpandLess sx={{ fontSize: 16, color: '#666' }} /> : <ExpandMore sx={{ fontSize: 16, color: '#666' }} />
                    ) : (
                        <Box width={24} />
                    )}
                </Box>

                <ListItemIcon sx={{ minWidth: 'auto !important' }}>
                    {hasChildren ? <ProjectIcon /> : <SubProjectIcon />}
                </ListItemIcon>

                <ListItemText
                    primary={item.Descripcion}
                    primaryTypographyProps={{
                        variant: 'body2',
                        noWrap: true,
                        fontSize: 11,
                        fontWeight: shouldHighlight ? 'bold' : 'normal',
                        color: shouldHighlight ? '#154360' : 'text.primary'
                    }}
                />
            </ListItemButton>

            {hasChildren && (
                <Collapse in={open} timeout="auto" unmountOnExit>
                    <List component="div" disablePadding sx={{
                        position: 'relative',
                        '&:before': {
                            content: '""',
                            position: 'absolute',
                            left: `${(item.Nivel - 1) * 12 + 19}px`,
                            top: 0,
                            bottom: 0,
                            borderLeft: '1px dashed rgba(0,0,0,0.1)'
                        }
                    }}>
                        {item.items.map((child: any) => (
                            <TreeItem
                                key={child.CodProyecto || child.CodClasificacion || child.CodInsumo}
                                item={child}
                                onSelect={onSelect}
                                selectedId={selectedId}
                                forceHighlight={shouldHighlight}
                            />
                        ))}
                    </List>
                </Collapse>
            )}
        </>
    );
};

interface TreeProps {
    data: any[];
    onSelect: (item: any) => void;
    selectedId: string | null;
}

export const RecursiveTree: React.FC<TreeProps> = ({ data, onSelect, selectedId }) => {
    if (!data || data.length === 0) {
        return <Box p={2}><Box component="span" sx={{ fontSize: 12, color: 'text.secondary' }}>No hay datos para mostrar</Box></Box>;
    }

    return (
        <List
            sx={{ width: '100%', maxWidth: '100%', bgcolor: 'background.paper' }}
            component="nav"
        >
            {data.map((item: any) => (
                <TreeItem
                    key={item.CodProyecto || item.CodClasificacion || item.CodInsumo}
                    item={item}
                    onSelect={onSelect}
                    selectedId={selectedId}
                />
            ))}
        </List>
    );
};
